import { convertToDarkerRgba } from '@design-system/theme';
/**
 * List styles that allow supporting both these features at the same time:
 * 1. Left-aligned list items have their first line indentation the same as their other lines
 * 2. List markers follow the text (as opposed to staying on the left) when a list item is aligned center or right
 */
const listStyles = () => `
  ul,
  ol {
    padding-left: 0;
    counter-reset: counter;
    margin-left: 40px;
  }

  ul li,
  ol li {
    list-style: none;
    counter-increment: counter;
    position: relative;
  }

  ul li[style*='text-align: center']::before,
  ul li[style*='text-align: right']::before,
  ol li[style*='text-align: center']::before,
  ol li[style*='text-align: right']::before {
    position: relative;
    left: -0.5em;
  }

  ul li::before,
  ol li::before {
    position: absolute;
    right: calc(100% + 0.5em);
    text-wrap: nowrap;
  }

  ul li::before {
    content: counter(counter, disc) ' ';
  }

  ol li::before {
    content: counter(counter) '. ';
  }

  ul[style*='list-style-type: circle'] > li::before {
    content: counter(counter, circle) ' ';
  }

  ul[style*='list-style-type: square'] > li::before {
    content: counter(counter, square) ' ';
  }

  ol[style*='list-style-type: lower-roman'] > li::before {
    content: counter(counter, lower-roman) '. ';
  }

  ol[style*='list-style-type: lower-alpha'] > li::before {
    content: counter(counter, lower-alpha) '. ';
  }

  ol[style*='list-style-type: lower-greek'] > li::before {
    content: counter(counter, lower-greek) '. ';
  }

  ol[style*='list-style-type: upper-roman'] > li::before {
    content: counter(counter, upper-roman) '. ';
  }

  ol[style*='list-style-type: upper-alpha'] > li::before {
    content: counter(counter, upper-alpha) '. ';
  }
`;
const linkStyles = (theme) => `
  a:not(.button),
  a:not(.button) span {
    color: ${theme.colors.link};
    text-decoration: underline;
  }

  a:not(.button):hover,
  a:not(.button):hover span {
    color: ${convertToDarkerRgba(theme.colors.link)};
  }

  strong a:not(.button),
  strong a:not(.button) span {
    font-weight: ${theme.fontWeights[1]};
    text-decoration-thickness: 2px;
  }
`;
const imageAndFigureStyles = () => `
  img {
    max-width: 100%;
    float: left;
    margin-right: 16px;
  }

  img[style*='float: right'] {
    margin-left: 16px;
    margin-right: 0px;
  }

  .img-left { float: left; }
  .img-right { float: right; }
  .img-center {
    float: none;
    margin: 0px;
    text-align: center;
  }

  figure {
    margin: 0px 16px 0px 0px;
    float: left;
    display: inline-block;
  }
  figure img { margin: 0px; }
  figure.align-center {
    margin: 0 auto;
    float: none;
    display: table;
  }

  figcaption {
    text-align: center;
    line-height: 2em;
  }
`;
const headingStyles = () => `
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-family: inherit;
    font-weight: 400;
    line-height: 1.2;
    color: inherit;
  }

  h1 { font-size: 2.1rem; margin-bottom: 2.1rem; }
  h2 { font-size: 1.5rem; margin-bottom: 1.5rem; }
  h3 { font-size: 1.4rem; margin-bottom: 1.4rem; }
  h4 { font-size: 1.3rem; margin-bottom: 1.3rem; }
  h5 { font-size: 1.2rem; margin-bottom: 1.2rem; }
  h6 { font-size: 1.1rem; margin-bottom: 1.1rem; }
`;
const editorStyles = () => `
  .mce-content-body [data-mce-selected='inline-boundary'] {
    outline: 1px solid #b4d7ff;
    outline-offset: 0;
    background-color: transparent;
  }

  blockquote {
    display: block;
    font-style: italic;
    font-family: Georgia, Times, 'Times New Roman', serif;
    padding: 2px 0px;
    border-style: solid;
    border-color: #ccc;
    border-width: 0px;
    margin: 1em 1.5em;
    padding-left: 20px;
    padding-right: 8px;
    border-left-width: 2px;
  }

  blockquote p {
    margin: 1em 0px;
  }
`;
const textStyles = (theme) => `
  p { line-height: 1.6;}

  strong, b { font-weight: ${theme.fontWeights[1]}; }

  i, cite, em, var, address, dfn { font-style: italic; }
  u, ins { text-decoration: underline; }
  s, strike, del { text-decoration: line-through; }

  sub { vertical-align: sub; }
  sup { vertical-align: super; }
  sub, sup { font-size: smaller; }

  tt, code, kbd, samp { font-family: monospace; }

  q { quotes: initial; }
  q::before { content: open-quote; }
  q::after { content: close-quote; }

  big { font-size: larger; }
  small { font-size: smaller; }

  .marker { background-color: Yellow; }
`;
const tableStyles = () => `
  table {
    border-collapse: collapse;
    font-size: 1em;
    width: 100%;
  }

  table td,
  table th {
    vertical-align: top;
  }

  table thead td,
  table th {
    font-weight: bold;
  }

  table[border] td,
  table[border] th,
  table[border] thead td {
    border: 1px solid currentColor;
  }

  table[border='0'],
  table[border='0'] td,
  table[border='0'] th {
    border: none;
  }
`;
const miscStyles = () => `
  iframe {
    max-width: 100%;
  }

  hr {
    border-color: #ccc;
  }

  .embedly-embed {
    max-width: 100%;
  }
`;
const htmlContentAdditionalStyles = () => `
  ul,
  ol {
    list-style: disc;
    padding: 0 40px;
    margin: 13px 0;
  }

  ol {
    list-style-type: decimal;
  }

  ul {
    list-style-type: disc;
  }

  figcaption {
    padding-top: 5px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: #2f363b;
  }

  p {
    margin-bottom: 20px;
  }
`;
export { listStyles, linkStyles, imageAndFigureStyles, headingStyles, editorStyles, textStyles, tableStyles, miscStyles, htmlContentAdditionalStyles, };
//# sourceMappingURL=htmlStyle.js.map